import { fetchEndpointData } from '@utils/fetchEndpointData';

import type {
  CustomerPortalOrderLocationsQuery,
  CustomerPortalOrderLocationsResponse,
} from './endpoints/CustomerPortalOrderLocationsEndpoint';
import type { CustomerPortalCreateOrderFromPdfResponse } from './endpoints/CustomerPortalCreateOrderFromPdfEndpoint';
import type {
  CustomerPortalOrderListItem,
  CustomerPortalOrderResponse,
} from './endpoints/CustomerPortalOrdersEndpoint.types';
import type {
  CustomerPortalPlaceOrderBody,
  CustomerPortalUpdateOrderBody,
  CustomerPortalFileComplaintBody,
  CustomerPortalOrderIdResponse,
} from './endpoints/CustomerPortalOrderMutations.types';

export interface CustomerPortalOrdersFilters {
  orderNumber?: string;
  customerRef?: string;
  unloadLocationCountry?: string;
  unloadLocationPostalCode?: string;
}

export async function fetchCustomerPortalOrders(args: {
  customerId: number;
  cursor?: string;
  take?: number;
  filters?: CustomerPortalOrdersFilters;
}): Promise<CustomerPortalOrderListItem[]> {
  const searchParams = new URLSearchParams();
  searchParams.append('customerId', args.customerId.toString());
  if (args.cursor != null) {
    searchParams.append('id', args.cursor);
  }
  if (args.take != null) {
    searchParams.append('take', args.take.toString());
  }
  if (args.filters?.orderNumber) {
    searchParams.append('orderNumber', args.filters.orderNumber);
  }
  if (args.filters?.customerRef) {
    searchParams.append('customerRef', args.filters.customerRef);
  }
  if (args.filters?.unloadLocationCountry) {
    searchParams.append('unloadLocationCountry', args.filters.unloadLocationCountry);
  }
  if (args.filters?.unloadLocationPostalCode) {
    searchParams.append('unloadLocationPostalCode', args.filters.unloadLocationPostalCode);
  }

  return fetchEndpointData<CustomerPortalOrderListItem[]>(`/api/customer-portal/orders?${searchParams.toString()}`, {
    method: 'GET',
  });
}

export async function fetchCustomerPortalOrder(id: string): Promise<CustomerPortalOrderResponse> {
  return fetchEndpointData<CustomerPortalOrderResponse>(`/api/customer-portal/orders/${id}`, {
    method: 'GET',
  });
}

export async function placeCustomerPortalOrder(
  data: CustomerPortalPlaceOrderBody,
): Promise<CustomerPortalOrderIdResponse> {
  return fetchEndpointData<CustomerPortalOrderIdResponse>('/api/customer-portal/orders/place', {
    method: 'POST',
    body: data,
  });
}

export async function updateCustomerPortalOrder(
  orderId: string,
  data: CustomerPortalUpdateOrderBody,
): Promise<CustomerPortalOrderIdResponse> {
  return fetchEndpointData<CustomerPortalOrderIdResponse>(`/api/customer-portal/orders/${orderId}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function sendCustomerPortalCmrReminder(orderId: string): Promise<CustomerPortalOrderIdResponse> {
  return fetchEndpointData<CustomerPortalOrderIdResponse>(`/api/customer-portal/orders/${orderId}/cmr-reminder`, {
    method: 'POST',
  });
}

export async function fileCustomerPortalComplaint(
  orderId: string,
  data: CustomerPortalFileComplaintBody,
): Promise<CustomerPortalOrderIdResponse> {
  return fetchEndpointData<CustomerPortalOrderIdResponse>(`/api/customer-portal/orders/${orderId}/complaints`, {
    method: 'POST',
    body: data,
  });
}

export async function fetchCustomerPortalOrderLocations(query: CustomerPortalOrderLocationsQuery) {
  const searchParams = new URLSearchParams();
  searchParams.append('type', query.type);
  if (query.customerId != null) {
    searchParams.append('customerId', query.customerId.toString());
  }

  const response = await fetchEndpointData<CustomerPortalOrderLocationsResponse>(
    `/api/customer-portal/order-locations?${searchParams.toString()}`,
    {
      method: 'GET',
    },
  );
  return response;
}

export async function createOrderFromPdf(documentId: string, customerId: number) {
  const response = await fetchEndpointData<CustomerPortalCreateOrderFromPdfResponse>(
    '/api/customer-portal/orders/create-from-pdf',
    {
      method: 'POST',
      body: { documentId, customerId },
    },
  );
  return response;
}
